import { Accordion, cn } from '@skupreme/shared/ui';
import { StyledButton, BorderButton, DangerButton } from '@elements/Button';
import { Warning } from '@phosphor-icons/react';
import { useAuth } from '@contexts/auth';
import { useConnectedCarrierAccounts } from '@hooks/connected-carrier-accounts';
import { CommonUtility } from '@utility/common';
import type { UseFormSetValue } from 'react-hook-form';
import {
  FulfillmentColumn,
  type FulfillmentColumnProps,
  type FulfillmentItem,
  type ShippingFormValues,
} from '@features/order-details';
import { AddressValidationContainer } from './styles';
import { ShippingCreate } from './ShippingCreate';

/* Pinned to the bottom of the overlay panel, which is the positioned box. */
const BUTTON_BOX =
  'sk:absolute sk:inset-x-0 sk:bottom-0 sk:z-100 sk:flex sk:flex-col sk:gap-2 sk:bg-surface';
const BUTTON_BOX_SHADOW = 'sk:p-4 sk:shadow-[0_-1px_2px_var(--sk-hairline)]';
const BUTTON_BOX_FLAT =
  'sk:border-t sk:border-solid sk:border-x-0 sk:border-b-0 sk:border-border-subtle sk:py-4';

type ShippingProps = Omit<
  FulfillmentColumnProps,
  'currentOrg' | 'connectedAccounts'
> & {
  noShadow?: boolean;
  variant?: 'panel' | 'page';
  isHazmat?: boolean;
  /* The overlay keeps one item list with checkboxes; the page splits the order
     into shipment cards, where an item belongs to exactly one of them. */
  availableItems?: FulfillmentItem[];
  toggleItems?: (item: FulfillmentItem, isChecked: boolean) => void;
  /** The overlay still lets a line ship in part; the page ships whole lines. */
  qtyChanged?: (item: FulfillmentItem, qty: number) => void;
  fulfilledLabels?: FulfillmentItem[];
  /** The overlay's dropdown opens on mount; the page lists the rates outright. */
  showRatesByDefault?: boolean;
  setValue?: UseFormSetValue<ShippingFormValues>;
  addressValidationData?: { error?: string; description?: string };
  addressValidationError?: string;
};

export function Shipping({
  addressValidationClick,
  autoFulfillerClicked,
  batchOrder,
  createLabel,
  disabledCreateLabel,
  disabledFulfillment,
  handleSubmit,
  invalidAddress,
  isHazmat,
  isNotManualFP,
  isUSPS,
  noShadow,
  orderError,
  rateLoading,
  shipmentsLoading,
  variant = 'panel',
  ...params
}: ShippingProps) {
  const { currentOrgInfo: currentOrg } = useAuth();
  const connectedAccounts = useConnectedCarrierAccounts(currentOrg?._id);

  const buttonBoxClass = cn(
    BUTTON_BOX,
    noShadow ? BUTTON_BOX_FLAT : BUTTON_BOX_SHADOW,
  );

  /* The details page has the room for the form itself, so it gets cards
     instead of the overlay's accordion and pinned footer. */
  if (variant === 'page') {
    return (
      <FulfillmentColumn
        addressValidationClick={addressValidationClick}
        autoFulfillerClicked={autoFulfillerClicked}
        batchOrder={batchOrder}
        createLabel={createLabel}
        disabledCreateLabel={disabledCreateLabel}
        disabledFulfillment={disabledFulfillment}
        handleSubmit={handleSubmit}
        invalidAddress={invalidAddress}
        isHazmat={isHazmat}
        isNotManualFP={isNotManualFP}
        isUSPS={isUSPS}
        orderError={orderError}
        rateLoading={rateLoading}
        shipmentsLoading={shipmentsLoading}
        currentOrg={currentOrg}
        connectedAccounts={connectedAccounts}
        {...params}
      />
    );
  }

  return (
    <div>
      <Accordion title="Shipping" defaultExpanded headingLevel={4}>
        <ShippingCreate
          addressValidationClick={addressValidationClick}
          invalidAddress={invalidAddress}
          isHazmat={isHazmat}
          isNotManualFP={isNotManualFP}
          isUSPS={isUSPS}
          rateLoading={rateLoading}
          currentOrg={currentOrg}
          connectedAccounts={connectedAccounts}
          {...params}
        />
      </Accordion>
      {isNotManualFP ? (
        <div className={buttonBoxClass}>
          <div className="sk:flex sk:gap-2">
            <StyledButton
              type="button"
              fluid
              onClick={() => autoFulfillerClicked()}
              disabled={invalidAddress || disabledFulfillment}
            >
              Fulfill Order
            </StyledButton>
            {currentOrg?.test && !CommonUtility.isProductionEnv() && (
              <BorderButton
                type="button"
                fluid
                onClick={() => autoFulfillerClicked(true)}
                disabled={invalidAddress || disabledFulfillment}
              >
                Test Fulfill Order
              </BorderButton>
            )}
          </div>

          {invalidAddress && (
            <DangerButton
              className="sk:flex! sk:items-center sk:justify-center"
              onClick={addressValidationClick}
              type="button"
              fluid
            >
              <Warning className="sk:mr-2" size={18} />
              Validate Address
            </DangerButton>
          )}
        </div>
      ) : (
        <div className={buttonBoxClass}>
          <div className="sk:flex sk:gap-2">
            <StyledButton
              disabled={(isUSPS && isHazmat) || disabledCreateLabel}
              fluid
              loading={rateLoading}
            >
              {batchOrder ? 'Save and Continue' : 'Create Label'}
            </StyledButton>
            {!CommonUtility.isProductionEnv() && (
              <BorderButton
                disabled={disabledCreateLabel}
                type="button"
                loading={rateLoading}
                fluid
                onClick={handleSubmit(data => createLabel(data, true))}
              >
                {batchOrder ? 'Save and Continue (Test)' : 'Create Test Label'}
              </BorderButton>
            )}
          </div>
          {invalidAddress && (
            <DangerButton
              className="sk:flex! sk:items-center sk:justify-center"
              onClick={addressValidationClick}
              type="button"
              fluid
            >
              <Warning className="sk:mr-2" size={18} />
              Validate Address
            </DangerButton>
          )}
          {orderError && (
            <AddressValidationContainer>
              {orderError}
            </AddressValidationContainer>
          )}
        </div>
      )}
    </div>
  );
}
